import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

interface PhoneProps {
  image: string;
  videoUrl?: string; 
  className?: string;
  video?: boolean; 
  parallax?: boolean;
  objectFit?: 'cover' | 'contain' | 'fill';
  borderless?: boolean;
  loading?: "lazy" | "eager"; 
}

export const Phone: React.FC<PhoneProps> = ({ 
  image, 
  videoUrl, 
  className = '', 
  video = false, 
  parallax = false,
  objectFit = 'cover',
  borderless = false,
  loading = "lazy"
}) => {
  const screenRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Rileva se è un link di Bunny.net (play o embed)
  const isBunny = videoUrl?.includes('iframe.mediadelivery.net');

  // URL del video diretto (senza ottimizzazione Cloudinary)
  const optimizedVideoUrl = videoUrl;
  
  const getProcessedUrl = () => {
    if (!videoUrl || !isBunny) return videoUrl;
    let baseUrl = videoUrl.replace('/play/', '/embed/');
    const cleanUrl = baseUrl.split('?')[0];
    return `${cleanUrl}?autoplay=true&loop=true&muted=true&preload=true&responsive=true&controls=false`;
  };

  const processedBunnyUrl = getProcessedUrl();

  // Intersection Observer per caricare il video SOLO quando è visibile
  useEffect(() => {
    if (!videoUrl || loading === "eager") {
      if (loading === "eager") setShouldLoadVideo(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setShouldLoadVideo(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" } // Inizia a caricare 200px prima che entri nello schermo
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [videoUrl, loading]);

  useEffect(() => {
    if (!parallax) return;

    const handleScroll = () => {
      if (!screenRef.current) return;
      const scrolled = window.scrollY;
      const val = Math.min(scrolled * 0.05, 50); 
      const scale = 1 + (scrolled * 0.0002);
      
      screenRef.current.style.transform = `translateY(${val}px) scale(${scale})`;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [parallax]);

  const containerClasses = borderless 
    ? `relative mx-auto rounded-[1.5rem] md:rounded-[2rem] h-auto aspect-[9/19] w-full flex flex-col overflow-hidden bg-zinc-900 ${className}`
    : `relative mx-auto border-zinc-800 bg-zinc-900 border-[6px] md:border-[10px] rounded-[1.5rem] md:rounded-[2rem] h-auto aspect-[9/19] w-full shadow-2xl flex flex-col overflow-hidden ${className}`;

  const screenRadius = borderless 
    ? 'rounded-[1.5rem] md:rounded-[2rem]' 
    : 'rounded-[1.2rem] md:rounded-[1.5rem]';

  return (
    <div ref={containerRef} className={containerClasses}>
      {!borderless && (
        <>
            <div className="h-[32px] w-[3px] bg-zinc-800 absolute -left-[6px] md:-left-[10px] top-[15%] rounded-l-lg"></div>
            <div className="h-[46px] w-[3px] bg-zinc-800 absolute -left-[6px] md:-left-[10px] top-[25%] rounded-l-lg"></div>
            <div className="h-[46px] w-[3px] bg-zinc-800 absolute -left-[6px] md:-left-[10px] top-[35%] rounded-l-lg"></div>
            <div className="h-[64px] w-[3px] bg-zinc-800 absolute -right-[6px] md:-right-[10px] top-[28%] rounded-r-lg"></div>
        </>
      )}
      
      {/* Screen Container */}
      <div className={`absolute top-0 w-full h-full bg-black overflow-hidden ${screenRadius}`}>
         
         {/* Media Layer */}
         <div 
            ref={screenRef}
            className="w-full h-full absolute top-0 left-0 transition-transform duration-100 ease-out"
         >
            {/* 
                POSTER IMAGE (Sempre presente ma ottimizzata)
                Funge da placeholder mentre il video carica.
            */}
            {image && !imageError ? (
              <img 
                src={image}
                alt="Phone Screen"
                loading={loading}
                decoding="async"
                fetchPriority={loading === "eager" ? "high" : "auto"}
                className={`absolute inset-0 w-full h-full object-${objectFit} z-0`}
                draggable={false}
                onError={() => setImageError(true)}
              />
            ) : (
               // Placeholder elegante se non c'è immagine (es. video S3 senza thumb)
               <div className="absolute inset-0 w-full h-full bg-zinc-900 z-0 flex items-center justify-center">
                  <div className="w-full h-full bg-gradient-to-br from-zinc-800 to-black opacity-50" />
               </div>
            )}

            {/* Spinner di caricamento (visibile solo se deve caricare video ma non è ancora pronto) */}
            {shouldLoadVideo && videoUrl && !isVideoLoaded && (
                <div className="absolute inset-0 flex items-center justify-center z-10 bg-black/40 backdrop-blur-sm transition-opacity duration-300">
                   <Loader2 className="w-10 h-10 text-white/70 animate-spin" />
                </div>
            )}

            {/* 
                VIDEO LAYER 
                Renderizzato solo se `shouldLoadVideo` è true (intersezione o eager)
            */}
            {shouldLoadVideo && videoUrl && (
              isBunny ? (
                <div className={`absolute inset-0 z-10 w-full h-full overflow-hidden bg-black transition-opacity duration-700 ${isVideoLoaded ? 'opacity-100' : 'opacity-0'}`}>
                   <iframe
                    src={processedBunnyUrl}
                    loading={loading}
                    onLoad={() => setIsVideoLoaded(true)}
                    style={{ 
                      position: 'absolute', 
                      top: '50%', 
                      left: '50%', 
                      width: '318%', 
                      height: '100%', 
                      transform: 'translate(-50%, -50%)',
                      border: 'none'
                    }}
                    allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
                    allowFullScreen={true}
                    title="Bunny Stream Content"
                  />
                </div>
              ) : (
                <video
                  src={optimizedVideoUrl}
                  poster={image || undefined} // Fallback nativo
                  autoPlay
                  muted
                  loop
                  playsInline
                  // preload="none" se non visibile, "metadata" se visibile
                  preload="metadata" 
                  className={`absolute inset-0 z-10 w-full h-full object-cover transition-opacity duration-1000 ${isVideoLoaded ? 'opacity-100' : 'opacity-0'}`}
                  onLoadedData={() => setIsVideoLoaded(true)}
                  onCanPlay={() => setIsVideoLoaded(true)}
                />
              )
            )}
            
            {/* Subtle Overlay Vignette */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40 pointer-events-none z-20" />
         </div>

        {/* Dynamic Island / Notch */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 h-[18px] md:h-[24px] w-[70px] md:w-[100px] bg-black rounded-b-2xl z-30 border-x border-b border-white/5"></div>

        {/* Fake Social UI Elements */}
        <div className="absolute bottom-10 left-6 right-6 z-30 flex flex-col gap-3 pointer-events-none opacity-30">
           <div className="flex items-center gap-2">
             <div className="w-8 h-8 rounded-full bg-white/40 backdrop-blur-md border border-white/10"></div>
             <div className="h-2 w-20 bg-white/30 backdrop-blur-md rounded-full"></div>
           </div>
           <div className="h-1.5 w-full bg-white/20 backdrop-blur-md rounded-full"></div>
        </div>
      </div>
    </div>
  );
};